import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import CallCenterModal from "../../components/call-center/listing/CallCenterModal";
import { toast } from "react-toastify";
import { useSearchParams, Link } from "react-router-dom";
import { FaWhatsapp } from "react-icons/fa";
import {
  FiCheckCircle,
  FiEdit3,
  FiFilter,
  FiInbox,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUpload,
  FiUserPlus,
} from "react-icons/fi";
import ConfirmDialog from "../../components/call-center/listing/ConfirmDialog";
import TicketAssignModal from "../../components/call-center/TicketAssignModal";
import PhoneNumberFieldArray from "../../components/call-center/PhoneNumberFieldArray";
import {
  formatPhoneDisplay,
  joinMobileNumberList,
  parseMobileNumberList,
} from "../../utils/phoneNumberUtils";
import TableSkeleton from "../../components/call-center/listing/TableSkeleton";
import ListingEmptyState from "../../components/call-center/listing/ListingEmptyState";
import ListingPaginationFooter from "../../components/call-center/listing/ListingPaginationFooter";
import ListingFilterHeader from "../../components/call-center/listing/ListingFilterHeader";
import StatusBadge from "../../components/call-center/listing/StatusBadge";
import {
  FILTER_FIELD_CLASS,
  FILTER_LABEL_CLASS,
  LISTING_CARD_CLASS,
  LISTING_CONTAINER_CLASS,
  LISTING_FILTER_CARD_CLASS,
  LISTING_ICON_ACTION_CLASS,
  LISTING_PAGE_CLASS,
  LISTING_PRIMARY_BUTTON_CLASS,
  LISTING_TABLE_HEAD_CLASS,
  LISTING_TABLE_HEADER_CELL_CLASS,
  LISTING_TABLE_ROW_CLASS,
} from "../../components/call-center/listing/listingStyles";
import { useTickets } from "../../hooks/useTickets";
import { useTicketStatuses } from "../../hooks/useTicketStatuses";
import { useCallCenterCalls } from "../../hooks/useCallCenterCalls";
import { DefaultTicketStatusKey, TicketPriority } from "../../service/CallCenterApi";
import {
  getTicket,
  loadAssignableAgentsForAssignment,
} from "../../service/CallCenterTickets";
import AppContext from "../../context/AppContext";
import useCallCenterT from "../../hooks/useCallCenterT";
import {
  buildTicketListFilterParams,
  buildTicketUpdateCustomFieldsDraft,
  getFilterColumns,
  getListColumns,
  getTicketFieldValue,
  getUpdateColumns,
  isColumnVisibleInFilter,
  isColumnVisibleInUpdate,
} from "../../components/call-center/ticketUploadUtils";
import TicketStatusBadge, { resolveTicketStatusMeta } from "../../components/call-center/TicketStatusBadge";
import TicketCallThread from "../../components/call-center/TicketCallThread";
import TicketWhatsAppThread from "../../components/call-center/TicketWhatsAppThread";
import TicketAttachments from "../../components/call-center/TicketAttachments";
import TicketAssignmentHistory from "../../components/call-center/TicketAssignmentHistory";
import PaymentProofModal from "../../components/call-center/PaymentProofModal";
import { useBrowserPhoneContext } from "../../modules/telephony/browser-phone/BrowserPhoneProvider";
import { initiateTicketBrowserCall, resolveTicketPhoneNumber } from "../../modules/telephony/browser-phone/initiateTicketCall";
import { useCallStream } from "../../hooks/useCallStream";
import { CallStatus } from "../../service/CallCenterApi";
import { getTelephonyErrorMessage } from "../../modules/telephony/browser-phone/telephonyErrorMessages";
import { isCallingEnabled } from "../../utils/orgFeatures";

const isTerminalCallStatus = (status) =>
  status === CallStatus.COMPLETED || status === CallStatus.FAILED;

const INITIAL_FILTERS = {
  accountValue: "",
  called: "all",
  statusIds: [],
  minOutstanding: "",
  maxOutstanding: "",
  fromPaymentDate: "",
  toPaymentDate: "",
  sortBy: "UPDATED_DESC",
  assigneeId: "",
  priority: "",
  remarks: "",
  customFields: {},
};

const INITIAL_PAGINATION = {
  page: 0,
  size: 20,
  totalPages: 0,
  totalElements: 0,
  hasNext: false,
  hasPrevious: false,
};

const isNotFoundError = (error) =>
  error?.response?.status === 404 ||
  error?.responseCode === 404 ||
  /not found/i.test(error?.message || "");


const formatDateInput = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const formatDateTimeLocalInput = (value) => {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

const toIsoFromDateTimeLocal = (value) => {
  if (!value) return undefined;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
};

const toOptionalNumber = (value) => {
  if (value === "" || value === null || typeof value === "undefined") return undefined;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const DEFAULT_STATUS_COLORS = {
  OPEN: "#3B82F6",
  FOLLOW_UP: "#F59E0B",
  PAID: "#10B981",
  FAILED: "#EF4444",
  CALLBACK_REQUIRED: "#8B5CF6",
};

const formatOmrAmount = (value) => {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  if (!raw) return "";
  if (raw.toUpperCase().includes("OMR")) return raw;
  const num = Number(raw);
  if (Number.isNaN(num)) return raw;
  return `OMR ${num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 3 })}`;
};

const priorityBadgeMeta = (priority, fallback = "—") => {
  const value = String(priority || "").toUpperCase();
  if (value === "HIGH") return { status: "ESCALATED_L1", label: value };
  if (value === "MEDIUM") return { status: "WARNING", label: value };
  if (value === "LOW") return { status: "SCHEDULED", label: value };
  return { status: "PENDING", label: priority || fallback };
};

const normalizeStatusOptions = (statuses, translate) => {
  if (!Array.isArray(statuses) || statuses.length === 0) {
    return Object.values(DefaultTicketStatusKey).map((statusKey) => ({
      id: statusKey,
      statusKey,
      label: statusKey,
      name: statusKey,
      colorCode: DEFAULT_STATUS_COLORS[statusKey] || "#6B7280",
    }));
  }
  return statuses.map((status, index) => ({
    id: status?.id ?? status?.statusId ?? status?.statusKey ?? `status-${index}`,
    statusKey: status?.statusKey ?? status?.key ?? status?.name ?? "",
    label: status?.name ?? status?.statusKey ?? status?.key ?? translate("ccUnknown"),
    name: status?.name ?? status?.statusKey ?? status?.key ?? "",
    colorCode: status?.colorCode || DEFAULT_STATUS_COLORS[status?.statusKey] || "#6B7280",
  }));
};

const CallCenterTicketsListPage = () => {
  const t = useCallCenterT();
  const callingEnabled = isCallingEnabled();
  const { userType } = useContext(AppContext);
  const [searchParams] = useSearchParams();
  const highlightTicketId = searchParams.get("ticketId");
  const deepLinkAssigneeId = searchParams.get("assigneeId");
  const deepLinkAccountNo = searchParams.get("accountNo");
  const highlightRowRef = useRef(null);
  const ticketsApi = useTickets();
  const statusesApi = useTicketStatuses();
  const { createManual, loading: callLoading } = useCallCenterCalls();
  const { store: browserPhoneStore, actions: browserPhoneActions } = useBrowserPhoneContext();

  const [tickets, setTickets] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [draftUpdates, setDraftUpdates] = useState({});
  const [selectedTicketIds, setSelectedTicketIds] = useState([]);
  const [deletingSelected, setDeletingSelected] = useState(false);
  const [updatingTicketId, setUpdatingTicketId] = useState(null);
  const [callingTicketId, setCallingTicketId] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [activeTicketId, setActiveTicketId] = useState(null);
  const [focusWhatsAppOnOpen, setFocusWhatsAppOnOpen] = useState(false);
  const [detailTab, setDetailTab] = useState("calls");
  const [threadRefreshKey, setThreadRefreshKey] = useState(0);
  const [assignmentHistoryRefreshKey, setAssignmentHistoryRefreshKey] = useState(0);
  const [activeCallLogId, setActiveCallLogId] = useState(null);
  const [pendingRecordingCallId, setPendingRecordingCallId] = useState(null);
  const [whatsappRefreshKey, setWhatsappRefreshKey] = useState(0);
  const [paymentProofOpen, setPaymentProofOpen] = useState(false);
  const { stateEvent: activeCallStreamEvent } = useCallStream(activeCallLogId);
  const whatsappSectionRef = useRef(null);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignModalMode, setAssignModalMode] = useState("single");
  const [assignTargetTicketIds, setAssignTargetTicketIds] = useState([]);
  const [assignInitialAssigneeId, setAssignInitialAssigneeId] = useState("");
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [officeUsersLoading, setOfficeUsersLoading] = useState(false);

  const [statusOptions, setStatusOptions] = useState(
    normalizeStatusOptions([], t)
  );
  const [columnConfig, setColumnConfig] = useState([]);
  const [officeUsers, setOfficeUsers] = useState([]);

  const sortOptions = useMemo(
    () => [
      { value: "OUTSTANDING_ASC", label: t("ccSortOutstandingAsc") },
      { value: "OUTSTANDING_DESC", label: t("ccSortOutstandingDesc") },
      { value: "PAYMENT_DATE_ASC", label: t("ccSortPaymentDateAsc") },
      { value: "PAYMENT_DATE_DESC", label: t("ccSortPaymentDateDesc") },
      { value: "CALLED_AT_ASC", label: t("ccSortCalledAtAsc") },
      { value: "CALLED_AT_DESC", label: t("ccSortCalledAtDesc") },
      { value: "UPDATED_DESC", label: t("ccSortRecentlyUpdated") },
    ],
    [t]
  );

  const formatDateTime = useCallback(
    (value) => {
      if (!value) return t("ccEmDash");
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return String(value);
      return parsed.toLocaleString();
    },
    [t]
  );

  const formatPaymentSource = useCallback(
    (source) => {
      switch (String(source || "").toUpperCase()) {
        case "WEBHOOK":
          return t("ccPaymentConfirmationSourceWebhook");
        case "REST_POLL":
          return t("ccPaymentConfirmationSourceRestPoll");
        case "MESSAGE_QUEUE":
          return t("ccPaymentConfirmationSourceMessageQueue");
        default:
          return source || t("ccEmDash");
      }
    },
    [t]
  );

  const formatPaymentAmount = useCallback(
    (amount) => {
      if (amount == null || amount === "") return t("ccEmDash");
      const parsed = Number(amount);
      if (Number.isNaN(parsed)) return String(amount);
      return parsed.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    [t]
  );

  const canManageAssignment = useMemo(() => {
    const normalized = String(userType || "").toLowerCase();
    return ["super_admin", "admin", "project_admin", "call_admin", "supervisor"].includes(normalized);
  }, [userType]);

  const canUseAssigneeFilter = canManageAssignment;

  const statusLookup = useMemo(() => {
    const lookup = {};
    statusOptions.forEach((item) => {
      if (item.statusKey) {
        lookup[item.statusKey] = item;
        lookup[item.statusKey.toUpperCase()] = item;
      }
      if (item.name) {
        lookup[item.name] = item;
      }
      if (item.label) {
        lookup[item.label] = item;
      }
    });
    return lookup;
  }, [statusOptions]);
  const listColumns = useMemo(() => getListColumns(columnConfig), [columnConfig]);
  const displayListColumns = useMemo(() => {
    const columns = listColumns.length
      ? listColumns
      : [
        { fieldKey: "accountNo", displayLabel: t("ccAccountNo") },
        { fieldKey: "mobileNumber", displayLabel: t("ccMobileNumber") },
        { fieldKey: "priority", displayLabel: t("ccPriority") },
        { fieldKey: "targetOutstanding", displayLabel: t("ccOutstandingRemarks") },
        { fieldKey: "status", displayLabel: t("ccStatus") },
        { fieldKey: "customerName", displayLabel: t("ccCustomerName") },
      ];
    const hasOutstanding = columns.some((column) => column.fieldKey === "targetOutstanding");
    if (!hasOutstanding) return columns;
    return columns
      .filter((column) => column.fieldKey !== "remarks")
      .map((column) =>
        column.fieldKey === "targetOutstanding"
          ? { ...column, displayLabel: t("ccOutstandingRemarks") }
          : column
      );
  }, [listColumns, t]);
  const filterColumns = useMemo(() => getFilterColumns(columnConfig), [columnConfig]);
  const updateColumns = useMemo(() => getUpdateColumns(columnConfig), [columnConfig]);
  const activeTicket = useMemo(
    () =>
      tickets.find(
        (ticket) =>
          activeTicketId != null
          && String(ticket.id).toLowerCase() === String(activeTicketId).toLowerCase()
      ) || null,
    [tickets, activeTicketId]
  );

  const openTicketById = useCallback(
    async (ticketId) => {
      if (!ticketId) return;
      try {
        const ticket = await getTicket(ticketId);
        const normalizedId = String(ticket.id).toLowerCase();
        setTickets((prev) => {
          const filtered = prev.filter(
            (item) => String(item.id).toLowerCase() !== normalizedId
          );
          return [ticket, ...filtered];
        });
        setActiveTicketId(ticket.id);
        setDetailTab("calls");
      } catch (error) {
        toast.error(error.message || t("ccToastLoadTicketsFailed"));
      }
    },
    [t]
  );

  const activeTicketStatusMeta = useMemo(() => {
    if (!activeTicket) {
      return null;
    }
    const statusValue =
      draftUpdates[activeTicket.id]?.status || activeTicket.statusKey || activeTicket.status;
    return resolveTicketStatusMeta(statusLookup, statusValue, activeTicket.status);
  }, [activeTicket, draftUpdates, statusLookup]);

  const buildListParams = (nextFilters = filters) => {
    const params = {
      called:
        nextFilters.called === "true"
          ? true
          : nextFilters.called === "false"
            ? false
            : undefined,
      statusIds: nextFilters.statusIds.length ? nextFilters.statusIds : undefined,
      minOutstanding: toOptionalNumber(nextFilters.minOutstanding),
      maxOutstanding: toOptionalNumber(nextFilters.maxOutstanding),
      fromPaymentDate: nextFilters.fromPaymentDate || undefined,
      toPaymentDate: nextFilters.toPaymentDate || undefined,
      sortBy: nextFilters.sortBy || undefined,
      assigneeId: canUseAssigneeFilter ? toOptionalNumber(nextFilters.assigneeId) : undefined,
    };
    const accountValue = nextFilters.accountValue.trim();
    if (accountValue) {
      params.accountNo = accountValue;
    }
    return {
      ...params,
      ...buildTicketListFilterParams(nextFilters),
    };
  };

  const updateCustomFieldFilter = (fieldKey, value) => {
    setFilters((prev) => ({
      ...prev,
      customFields: {
        ...(prev.customFields || {}),
        [fieldKey]: value,
      },
    }));
  };

  const renderDynamicFilterField = (column) => {
    const label = column.displayLabel || column.fieldKey;
    const inputClassName = FILTER_FIELD_CLASS;

    if (column.fieldKey === "priority") {
      return (
        <div key={`filter-${column.fieldKey}`} className="lg:col-span-2">
          <label className={FILTER_LABEL_CLASS}>
            {label}
          </label>
          <select
            value={filters.priority}
            onChange={(event) => setFilters((prev) => ({ ...prev, priority: event.target.value }))}
            className={inputClassName}
          >
            <option value="">{t("ccAll")}</option>
            {Object.values(TicketPriority).map((priority) => (
              <option key={`filter-priority-${priority}`} value={priority}>
                {priority}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (column.fieldKey === "remarks") {
      return (
        <div key={`filter-${column.fieldKey}`} className="lg:col-span-2">
          <label className={FILTER_LABEL_CLASS}>
            {label}
          </label>
          <input
            type="text"
            value={filters.remarks}
            onChange={(event) => setFilters((prev) => ({ ...prev, remarks: event.target.value }))}
            className={inputClassName}
            placeholder={label}
          />
        </div>
      );
    }

    const inputType = column.dataType === "number" ? "number" : column.dataType === "date" ? "date" : "text";
    return (
      <div key={`filter-${column.fieldKey}`} className="lg:col-span-2">
        <label className={FILTER_LABEL_CLASS}>
          {label}
        </label>
        <input
          type={inputType}
          value={filters.customFields?.[column.fieldKey] || ""}
          onChange={(event) => updateCustomFieldFilter(column.fieldKey, event.target.value)}
          className={inputClassName}
          placeholder={label}
        />
      </div>
    );
  };

  const loadTickets = async (
    nextFilters = filters,
    page = pagination.page,
    size = pagination.size
  ) => {
    setListLoading(true);
    setListError("");
    try {
      const response = await ticketsApi.list({
        ...buildListParams(nextFilters),
        page,
        size,
      });
      const isPaginated = response && typeof response === "object" && Array.isArray(response.items);
      const nextTickets = isPaginated ? response.items : Array.isArray(response) ? response : [];
      setTickets(nextTickets);
      if (isPaginated && Array.isArray(response.columns)) {
        setColumnConfig(response.columns);
      }
      setPagination(
        isPaginated
          ? {
            page: response.page ?? page,
            size: response.size ?? size,
            totalPages: response.totalPages ?? 0,
            totalElements: response.totalElements ?? nextTickets.length,
            hasNext: Boolean(response.hasNext),
            hasPrevious: Boolean(response.hasPrevious),
          }
          : {
            page,
            size,
            totalPages: nextTickets.length ? 1 : 0,
            totalElements: nextTickets.length,
            hasNext: false,
            hasPrevious: false,
          }
      );
      setSelectedTicketIds((prevSelected) =>
        prevSelected.filter((id) => nextTickets.some((ticket) => ticket.id === id))
      );
      const nextColumnConfig = isPaginated && Array.isArray(response.columns) ? response.columns : columnConfig;
      const nextUpdateColumns = getUpdateColumns(nextColumnConfig);
      setDraftUpdates(
        nextTickets.reduce((acc, ticket) => {
          const mobileSource =
            ticket.customFields?.mobileNumber ?? ticket.mobileNumber ?? "";
          acc[ticket.id] = {
            status: ticket.statusKey || ticket.status || "",
            priority: ticket.priority || "",
            remarksComment: ticket.remarksComment || ticket.remarks || "",
            additionalComments: ticket.additionalComments || "",
            targetOutstanding:
              typeof ticket.targetOutstanding === "number" ? String(ticket.targetOutstanding) : "",
            targetPaymentDate: formatDateInput(ticket.targetPaymentDate),
            calledAt: formatDateTimeLocalInput(ticket.calledAt),
            mobileNumbers: parseMobileNumberList(mobileSource),
            customFields: buildTicketUpdateCustomFieldsDraft(ticket, nextUpdateColumns),
          };
          return acc;
        }, {})
      );
    } catch (error) {
      const message = error.message || t("ccTicketsListLoadFailed");
      setListError(message);
      toast.error(message);
    } finally {
      setListLoading(false);
    }
  };

  const loadTicketStatuses = async () => {
    try {
      const data = await statusesApi.list();
      setStatusOptions(normalizeStatusOptions(data, t));
    } catch (error) {
      setStatusOptions(normalizeStatusOptions([], t));
      toast.error(error.message || t("ccToastLoadStatusOptionsFailed"));
    }
  };

  const loadOfficeUsers = async () => {
    if (!canManageAssignment) {
      setOfficeUsers([]);
      return;
    }
    try {
      const users = await loadAssignableAgentsForAssignment();
      setOfficeUsers(Array.isArray(users) ? users : []);
    } catch (error) {
      setOfficeUsers([]);
      toast.error(error.message || t("ccToastLoadAssigneesFailed"));
    }
  };

  const officeUserOptions = useMemo(
    () =>
      officeUsers
        .map((user) => {
          const userId = user?.userId ?? user?.id;
          if (userId == null || userId === "") {
            return null;
          }
          return {
            userId: String(userId),
            label: user?.name || user?.email || `${t("ccUserFallback")} ${userId}`,
          };
        })
        .filter(Boolean)
        .sort((left, right) => left.label.localeCompare(right.label)),
    [officeUsers, t]
  );

  const assigneeNameById = useMemo(() => {
    const lookup = {};
    officeUserOptions.forEach((user) => {
      lookup[user.userId] = user.label;
    });
    return lookup;
  }, [officeUserOptions]);

  const resolveAssigneeLabel = useCallback(
    (ticket) => {
      const assigneeId = ticket?.assignedTo;
      if (assigneeId == null || assigneeId === "") {
        return t("ccUnassigned");
      }
      return assigneeNameById[String(assigneeId)] || `#${assigneeId}`;
    },
    [assigneeNameById, t]
  );

  const openAssignModal = async (ticketIds, options = {}) => {
    if (!canManageAssignment || !ticketIds?.length) return;
    const ids = [...new Set(ticketIds)];
    setAssignModalMode(ids.length > 1 ? "bulk" : "single");
    setAssignTargetTicketIds(ids);
    setAssignInitialAssigneeId(
      options.initialAssigneeId != null ? String(options.initialAssigneeId) : ""
    );
    setAssignModalOpen(true);
    setOfficeUsersLoading(true);
    try {
      const users = await loadAssignableAgentsForAssignment();
      setOfficeUsers(Array.isArray(users) ? users : []);
      if (!users?.length) {
        toast.error(t("ccTicketAssignNoAgents"));
      }
    } catch (error) {
      setOfficeUsers([]);
      toast.error(error.message || t("ccToastLoadAssigneesFailed"));
    } finally {
      setOfficeUsersLoading(false);
    }
  };

  const closeAssignModal = () => {
    if (assignSubmitting) return;
    setAssignModalOpen(false);
    setAssignTargetTicketIds([]);
    setAssignInitialAssigneeId("");
  };

  const applyAssignmentResultToTickets = (updatedTickets) => {
    if (!Array.isArray(updatedTickets) || updatedTickets.length === 0) return;
    const byId = updatedTickets.reduce((acc, ticket) => {
      if (ticket?.id) acc[ticket.id] = ticket;
      return acc;
    }, {});
    setTickets((prev) =>
      prev.map((row) => (byId[row.id] ? { ...row, ...byId[row.id] } : row))
    );
  };

  const handleConfirmAssignment = async (assignedTo) => {
    if (!assignedTo || assignTargetTicketIds.length === 0) return;
    setAssignSubmitting(true);
    try {
      if (assignModalMode === "bulk") {
        const response = await ticketsApi.bulkAssign({
          ticketIds: assignTargetTicketIds,
          assignedTo,
        });
        await loadTickets(filters, pagination.page, pagination.size);
        const assignedCount = response?.assignedCount ?? assignTargetTicketIds.length;
        toast.success(`${t("ccTicketsBulkAssignSuccess")} (${assignedCount})`);
        setSelectedTicketIds((prev) => prev.filter((id) => !assignTargetTicketIds.includes(id)));
      } else {
        const ticketId = assignTargetTicketIds[0];
        const updated = await ticketsApi.assign({ id: ticketId, assignedTo });
        applyAssignmentResultToTickets([updated]);
        toast.success(t("ccTicketAssignSuccess"));
      }
      closeAssignModal();
      setAssignmentHistoryRefreshKey((value) => value + 1);
    } catch (error) {
      toast.error(error.message || t("ccTicketAssignFailed"));
    } finally {
      setAssignSubmitting(false);
    }
  };

  const handleUnassignTicket = async () => {
    if (assignTargetTicketIds.length !== 1) return;
    setAssignSubmitting(true);
    try {
      const ticketId = assignTargetTicketIds[0];
      const updated = await ticketsApi.unassign({ id: ticketId });
      applyAssignmentResultToTickets([updated]);
      toast.success(t("ccTicketUnassignSuccess"));
      closeAssignModal();
      setAssignmentHistoryRefreshKey((value) => value + 1);
    } catch (error) {
      toast.error(error.message || t("ccTicketUnassignFailed"));
    } finally {
      setAssignSubmitting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const initialFilters = {
      ...INITIAL_FILTERS,
      ...(deepLinkAssigneeId && canUseAssigneeFilter
        ? { assigneeId: deepLinkAssigneeId }
        : {}),
      ...(deepLinkAccountNo ? { accountValue: deepLinkAccountNo } : {}),
    };
    if (deepLinkAssigneeId || deepLinkAccountNo) {
      setFilters(initialFilters);
    }

    const bootstrap = async () => {
      await loadTickets(initialFilters, 0, INITIAL_PAGINATION.size);
      if (!cancelled && highlightTicketId) {
        await openTicketById(highlightTicketId);
      }
    };

    loadTicketStatuses();
    loadOfficeUsers();
    bootstrap();

    return () => {
      cancelled = true;
    };
  }, [
    canUseAssigneeFilter,
    deepLinkAssigneeId,
    deepLinkAccountNo,
    highlightTicketId,
    openTicketById,
  ]);

  useEffect(() => {
    if (!highlightTicketId || tickets.length === 0) return;
    const timerId = window.setTimeout(() => {
      highlightRowRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 150);
    return () => window.clearTimeout(timerId);
  }, [highlightTicketId, tickets, activeTicketId]);

  const allVisibleTicketIds = useMemo(() => tickets.map((ticket) => ticket.id), [tickets]);
  const allVisibleSelected =
    allVisibleTicketIds.length > 0 &&
    allVisibleTicketIds.every((ticketId) => selectedTicketIds.includes(ticketId));

  const updateDraft = (ticketId, patch) => {
    setDraftUpdates((prev) => ({
      ...prev,
      [ticketId]: {
        ...(prev[ticketId] || {}),
        ...patch,
      },
    }));
  };

  const updateDraftCustomField = (ticketId, fieldKey, value) => {
    setDraftUpdates((prev) => ({
      ...prev,
      [ticketId]: {
        ...(prev[ticketId] || {}),
        customFields: {
          ...((prev[ticketId] || {}).customFields || {}),
          [fieldKey]: value,
        },
      },
    }));
  };

  const updateDraftMobileNumbers = (ticketId, nextNumbers) => {
    const joined = joinMobileNumberList(nextNumbers);
    setDraftUpdates((prev) => ({
      ...prev,
      [ticketId]: {
        ...(prev[ticketId] || {}),
        mobileNumbers: nextNumbers,
        customFields: {
          ...((prev[ticketId] || {}).customFields || {}),
          mobileNumber: joined,
        },
      },
    }));
  };

  const renderUpdateColumnInput = (column, ticketId) => {
    const draft = draftUpdates[ticketId] || {};
    const value = draft.customFields?.[column.fieldKey] ?? "";
    const disabled = updatingTicketId === ticketId;
    const label = column.displayLabel || column.fieldKey;
    const inputType = column.dataType === "number" ? "number" : column.dataType === "date" ? "date" : "text";

    if (column.fieldKey === "mobileNumber") {
      const list = draft.mobileNumbers?.length
        ? draft.mobileNumbers
        : parseMobileNumberList(value);
      return (
        <div key={`update-${column.fieldKey}`} className="md:col-span-2">
          <label className={FILTER_LABEL_CLASS}>{label}</label>
          <PhoneNumberFieldArray
            values={list}
            onChange={(next) => updateDraftMobileNumbers(ticketId, next)}
            disabled={disabled}
            addLabel={t("ccAddMobileNumber")}
            removeLabel={t("ccRemoveMobileNumber")}
            placeholder={t("ccMobileNumberPlaceholder")}
            hint={t("ccMobileNumberHint")}
            fieldLabelPrefix={t("ccMobileNumber")}
            showFieldLabels
            t={t}
          />
        </div>
      );
    }

    return (
      <div key={`update-${column.fieldKey}`}>
        <label className={FILTER_LABEL_CLASS}>{label}</label>
        <input
          type={inputType}
          value={value}
          onChange={(event) => updateDraftCustomField(ticketId, column.fieldKey, event.target.value)}
          className={FILTER_FIELD_CLASS}
          disabled={disabled}
        />
      </div>
    );
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedTicketIds((prev) =>
        prev.filter((ticketId) => !allVisibleTicketIds.includes(ticketId))
      );
      return;
    }
    setSelectedTicketIds((prev) => Array.from(new Set([...prev, ...allVisibleTicketIds])));
  };

  const toggleTicketSelection = (ticketId) => {
    setSelectedTicketIds((prev) =>
      prev.includes(ticketId)
        ? prev.filter((id) => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const openTicketDetails = (ticket, options = {}) => {
    setActiveTicketId(ticket.id);
    const focusWhatsApp = Boolean(options.focusWhatsApp);
    setFocusWhatsAppOnOpen(focusWhatsApp);
    setDetailTab(focusWhatsApp ? "whatsapp" : "calls");
  };

  const closeTicketDetails = () => {
    setActiveTicketId(null);
    setFocusWhatsAppOnOpen(false);
    setDetailTab("calls");
    setActiveCallLogId(null);
    setPendingRecordingCallId(null);
    setPaymentProofOpen(false);
  };

  useEffect(() => {
    if (!activeCallLogId || !activeCallStreamEvent) {
      return;
    }
    const hasRecording = Boolean(String(activeCallStreamEvent.recordingUrl || "").trim());
    const terminal = isTerminalCallStatus(activeCallStreamEvent.status);
    if (hasRecording) {
      setThreadRefreshKey((prev) => prev + 1);
      setPendingRecordingCallId(null);
      setActiveCallLogId(null);
      return;
    }
    if (terminal) {
      setThreadRefreshKey((prev) => prev + 1);
      setPendingRecordingCallId(activeCallLogId);
      setActiveCallLogId(null);
    }
  }, [activeCallLogId, activeCallStreamEvent]);

  useEffect(() => {
    if (!activeTicketId || !focusWhatsAppOnOpen) {
      return;
    }
    const timerId = window.setTimeout(() => {
      whatsappSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      setFocusWhatsAppOnOpen(false);
    }, 150);
    return () => window.clearTimeout(timerId);
  }, [activeTicketId, focusWhatsAppOnOpen, detailTab]);

  const handleMakeCall = async (ticket) => {
    if (!ticket) return;
    try {
      setCallingTicketId(ticket.id);
      const callResponse = await initiateTicketBrowserCall({
        ticket,
        getFieldValue: getTicketFieldValue,
        createManual,
        browserPhoneStore,
        browserPhoneActions,
        toast,
        t,
      });
      if (callResponse?.id) {
        setActiveCallLogId(callResponse.id);
        setPendingRecordingCallId(null);
      }
    } catch (error) {
      toast.error(getTelephonyErrorMessage(error, t));
    } finally {
      setCallingTicketId(null);
      setThreadRefreshKey((prev) => prev + 1);
    }
  };

  const handleStatusUpdate = async (ticket) => {
    const draft = draftUpdates[ticket.id] || {};
    try {
      setUpdatingTicketId(ticket.id);
      await ticketsApi.patchStatus({
        id: ticket.id,
        status: draft.status || undefined,
        priority: draft.priority || undefined,
        remarksComment: draft.remarksComment ?? undefined,
        additionalComments: draft.additionalComments ?? undefined,
        targetOutstanding: toOptionalNumber(draft.targetOutstanding),
        targetPaymentDate: draft.targetPaymentDate || undefined,
        calledAt: toIsoFromDateTimeLocal(draft.calledAt),
        customFields: draft.customFields,
      });

      setTickets((prev) =>
        prev.map((row) =>
          row.id === ticket.id
            ? {
              ...row,
              statusKey: draft.status || row.statusKey,
              status: resolveTicketStatusMeta(
                statusLookup,
                draft.status || row.statusKey || row.status,
                row.status
              ).label,
              priority: draft.priority || row.priority,
              remarksComment: draft.remarksComment ?? row.remarksComment ?? row.remarks,
              remarks: draft.remarksComment ?? row.remarks,
              additionalComments:
                draft.additionalComments ?? row.additionalComments ?? "",
              targetOutstanding:
                typeof toOptionalNumber(draft.targetOutstanding) === "number"
                  ? toOptionalNumber(draft.targetOutstanding)
                  : row.targetOutstanding,
              targetPaymentDate: draft.targetPaymentDate || row.targetPaymentDate,
              calledAt: toIsoFromDateTimeLocal(draft.calledAt) || row.calledAt,
              customFields: {
                ...(row.customFields || {}),
                ...(draft.customFields || {}),
              },
            }
            : row
        )
      );
      toast.success(t("ccToastTicketStatusUpdated"));
      setThreadRefreshKey((prev) => prev + 1);
      if (draft.mobileNumbers?.length || draft.customFields?.mobileNumber) {
        setWhatsappRefreshKey((prev) => prev + 1);
      }
    } catch (error) {
      if (isNotFoundError(error)) {
        setTickets((prev) => prev.filter((row) => row.id !== ticket.id));
        setSelectedTicketIds((prev) => prev.filter((id) => id !== ticket.id));
        toast.error(t("ccToastTicketNotFound"));
        return;
      }
      toast.error(error.message || t("ccToastTicketStatusUpdateFailed"));
    } finally {
      setUpdatingTicketId(null);
    }
  };

  const requestBulkDelete = () => {
    if (selectedTicketIds.length === 0) {
      toast.info(t("ccToastSelectTicketToDelete"));
      return;
    }
    setBulkDeleteConfirmOpen(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedTicketIds.length === 0) return;

    try {
      setDeletingSelected(true);
      const response = await ticketsApi.bulkSoftDelete({
        ticketIds: selectedTicketIds,
      });

      const removedIds = new Set([
        ...(Array.isArray(response?.deletedTicketIds) ? response.deletedTicketIds : []),
        ...(Array.isArray(response?.alreadyDeletedTicketIds)
          ? response.alreadyDeletedTicketIds
          : []),
        ...(Array.isArray(response?.notFoundTicketIds) ? response.notFoundTicketIds : []),
      ]);

      setTickets((prev) => prev.filter((ticket) => !removedIds.has(ticket.id)));
      setSelectedTicketIds([]);
      setDraftUpdates((prev) => {
        const next = { ...prev };
        removedIds.forEach((id) => {
          delete next[id];
        });
        return next;
      });

      toast.success(
        `${t("ccToastDeleteProcessedPrefix")} ${response?.deletedCount || 0}, ${t("ccToastDeleteAlreadyDeleted")} ${response?.alreadyDeletedCount || 0
        }, ${t("ccToastDeleteNotFound")} ${response?.notFoundCount || 0}.`
      );
      setBulkDeleteConfirmOpen(false);
    } catch (error) {
      toast.error(error.message || t("ccToastBulkDeleteFailed"));
    } finally {
      setDeletingSelected(false);
    }
  };

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        filters.accountValue.trim() ||
          filters.called !== "all" ||
          filters.statusIds.length > 0 ||
          filters.minOutstanding ||
          filters.maxOutstanding ||
          filters.fromPaymentDate ||
          filters.toPaymentDate ||
          filters.assigneeId ||
          filters.priority ||
          filters.remarks ||
          Object.values(filters.customFields || {}).some((value) => String(value || "").trim())
      ),
    [filters]
  );

  const applyFilters = () => loadTickets(filters, 0, pagination.size);

  const resetFilters = () => {
    setFilters(INITIAL_FILTERS);
    loadTickets(INITIAL_FILTERS, 0, pagination.size);
  };

  const orgName =
    (typeof localStorage !== "undefined" && localStorage.getItem("selectedOrgName")) || "EEMAD";
  const refreshTickets = () => loadTickets(filters, pagination.page, pagination.size);
  const closeBulkDeleteConfirm = () => {
    if (deletingSelected) return;
    setBulkDeleteConfirmOpen(false);
  };
  const ticketResultsLabel =
    pagination.totalElements !== 1 ? t("ccTotalTickets") : t("ccTotalTicket");
  const skeletonColumnCount = displayListColumns.length + 2;

  return (
    <div className={LISTING_PAGE_CLASS}>
      <div className={LISTING_CONTAINER_CLASS}>
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-[28px] font-bold tracking-tight text-[#111827]">
              {t("ccTicketsTitle")}
            </h1>
            <p className="mt-1 text-sm text-[#6b7280]">
              {t("ccTicketsSubtitle").replace("{org}", orgName)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to="/call-center/agent-workspace"
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <FiEdit3 className="h-4 w-4" />
              {t("ccMenuAgentWorkspace")}
            </Link>
            <Link
              to="/call-center/ticket-bulk-upload"
              className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
            >
              <FiUpload className="h-4 w-4" />
              {t("ccMenuTicketBulkUpload")}
            </Link>
            <Link to="/call-center/agent-workspace" className={LISTING_PRIMARY_BUTTON_CLASS}>
              <FiPlus className="h-4 w-4" />
              {t("ccNewTicket")}
            </Link>
          </div>
        </header>

        {listError && (
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <span>{listError}</span>
            <button type="button" onClick={refreshTickets} className="font-semibold hover:underline">
              {t("ccRefresh")}
            </button>
          </div>
        )}

        <section className={LISTING_FILTER_CARD_CLASS}>
          <ListingFilterHeader
            title={t("ccListFiltersTitle")}
            page={pagination.page}
            totalPages={pagination.totalPages}
            totalElements={pagination.totalElements}
            resultsLabel={ticketResultsLabel}
            showingPageLabel={t("ccShowingPage")}
            ofLabel={t("ccOf")}
            refreshLabel={t("ccRefresh")}
            loading={listLoading}
            onRefresh={refreshTickets}
            actions={
              <button
                type="button"
                onClick={() => setShowAdvancedFilters((prev) => !prev)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[#1d4ed8] transition hover:text-[#1e40af]"
              >
                <FiFilter className="h-4 w-4" />
                {showAdvancedFilters ? t("ccHideAdvancedFilters") : t("ccShowAdvancedFilters")}
              </button>
            }
          />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
            {isColumnVisibleInFilter(columnConfig, "accountNo") && (
              <div className="lg:col-span-5">
                <label className={FILTER_LABEL_CLASS}>
                  {columnConfig.find((column) => column.fieldKey === "accountNo")?.displayLabel || t("ccAccountNo")}
                </label>
                <div className="flex gap-2">
                  <input
                    value={filters.accountValue}
                    onChange={(event) =>
                      setFilters((prev) => ({ ...prev, accountValue: event.target.value }))
                    }
                    onKeyDown={(event) => event.key === "Enter" && applyFilters()}
                    className={FILTER_FIELD_CLASS}
                    placeholder={t("ccSearchAccountPlaceholder")}
                  />
                  <button
                    type="button"
                    onClick={applyFilters}
                    className="inline-flex h-10 shrink-0 items-center rounded-xl bg-[#2B7FFF] px-3 text-sm font-semibold text-white hover:bg-[#1d6ef2]"
                    aria-label={t("ccApplyFilters")}
                  >
                    <FiSearch className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            <div className={isColumnVisibleInFilter(columnConfig, "accountNo") ? "lg:col-span-2" : "lg:col-span-3"}>
              <label className={FILTER_LABEL_CLASS}>
                {t("ccCalled")}
              </label>
              <select
                value={filters.called}
                onChange={(event) => setFilters((prev) => ({ ...prev, called: event.target.value }))}
                className={FILTER_FIELD_CLASS}
              >
                <option value="all">{t("ccAll")}</option>
                <option value="true">{t("ccCalledOnly")}</option>
                <option value="false">{t("ccNotCalled")}</option>
              </select>
            </div>

            <div className={isColumnVisibleInFilter(columnConfig, "accountNo") ? "lg:col-span-3" : "lg:col-span-4"}>
              <label className={FILTER_LABEL_CLASS}>
                {t("ccAssignee")}
              </label>
              {canUseAssigneeFilter ? (
                <select
                  value={filters.assigneeId}
                  onChange={(event) =>
                    setFilters((prev) => ({ ...prev, assigneeId: event.target.value }))
                  }
                  className={FILTER_FIELD_CLASS}
                >
                  <option value="">{t("ccAllAssignees")}</option>
                  {officeUserOptions.map((user) => (
                    <option key={user.userId} value={user.userId}>
                      {user.label}
                    </option>
                  ))}
                </select>
              ) : (
                <div className={`${FILTER_FIELD_CLASS} flex items-center text-sm text-slate-400`}>
                  {t("ccScopedToCurrentAgent")}
                </div>
              )}
            </div>

            <div className={isColumnVisibleInFilter(columnConfig, "accountNo") ? "lg:col-span-2" : "lg:col-span-3"}>
              <label className={FILTER_LABEL_CLASS}>
                {t("ccSortBy")}
              </label>
              <select
                value={filters.sortBy}
                onChange={(event) => setFilters((prev) => ({ ...prev, sortBy: event.target.value }))}
                className={FILTER_FIELD_CLASS}
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {showAdvancedFilters && (
            <div className="mt-4 border-t border-slate-100 pt-4">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                {isColumnVisibleInFilter(columnConfig, "status") && (
                  <div className="lg:col-span-4">
                    <label className={FILTER_LABEL_CLASS}>
                      {columnConfig.find((column) => column.fieldKey === "status")?.displayLabel || t("ccStatusIds")}
                    </label>
                    <select
                      multiple
                      value={filters.statusIds}
                      onChange={(event) =>
                        setFilters((prev) => ({
                          ...prev,
                          statusIds: Array.from(event.target.selectedOptions).map((option) => option.value),
                        }))
                      }
                      className={`${FILTER_FIELD_CLASS} h-28 py-2`}
                    >
                      {statusOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-[11px] text-slate-400">
                      {filters.statusIds.length > 0
                        ? `${filters.statusIds.length} ${t("ccStatusFiltersSelected")}`
                        : t("ccMultiSelectTip")}
                    </p>
                  </div>
                )}

                {isColumnVisibleInFilter(columnConfig, "targetOutstanding") && (
                  <>
                    <div className="lg:col-span-2">
                      <label className={FILTER_LABEL_CLASS}>
                        {t("ccMinOutstanding")}
                      </label>
                      <input
                        type="number"
                        step="any"
                        value={filters.minOutstanding}
                        onChange={(event) =>
                          setFilters((prev) => ({ ...prev, minOutstanding: event.target.value }))
                        }
                        className={FILTER_FIELD_CLASS}
                        placeholder="0.00"
                      />
                    </div>

                    <div className="lg:col-span-2">
                      <label className={FILTER_LABEL_CLASS}>
                        {t("ccMaxOutstanding")}
                      </label>
                      <input
                        type="number"
                        step="any"
                        value={filters.maxOutstanding}
                        onChange={(event) =>
                          setFilters((prev) => ({ ...prev, maxOutstanding: event.target.value }))
                        }
                        className={FILTER_FIELD_CLASS}
                        placeholder="0.00"
                      />
                    </div>
                  </>
                )}

                {isColumnVisibleInFilter(columnConfig, "targetPaymentDate") && (
                  <>
                    <div className="lg:col-span-2">
                      <label className={FILTER_LABEL_CLASS}>
                        {t("ccPaymentDateFrom")}
                      </label>
                      <input
                        type="date"
                        value={filters.fromPaymentDate}
                        onChange={(event) =>
                          setFilters((prev) => ({ ...prev, fromPaymentDate: event.target.value }))
                        }
                        className={FILTER_FIELD_CLASS}
                      />
                    </div>

                    <div className="lg:col-span-2">
                      <label className={FILTER_LABEL_CLASS}>
                        {t("ccPaymentDateTo")}
                      </label>
                      <input
                        type="date"
                        value={filters.toPaymentDate}
                        onChange={(event) =>
                          setFilters((prev) => ({ ...prev, toPaymentDate: event.target.value }))
                        }
                        className={FILTER_FIELD_CLASS}
                      />
                    </div>
                  </>
                )}

                {filterColumns.map((column) => renderDynamicFilterField(column))}
              </div>
            </div>
          )}
          <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-4">
            <button
              type="button"
              onClick={applyFilters}
              disabled={listLoading}
              className={LISTING_PRIMARY_BUTTON_CLASS}
            >
              {listLoading ? t("ccLoading") : t("ccApplyFilters")}
            </button>
            <button
              type="button"
              onClick={resetFilters}
              disabled={!hasActiveFilters || listLoading}
              className="h-10 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {t("ccClearFilters")}
            </button>
            {canManageAssignment ? (
              <button
                type="button"
                onClick={() => openAssignModal(selectedTicketIds)}
                disabled={selectedTicketIds.length === 0 || assignSubmitting}
                className="ml-auto inline-flex h-10 items-center gap-1.5 rounded-xl border border-[#2B7FFF]/30 bg-[#2B7FFF]/10 px-4 text-sm font-semibold text-[#1d4ed8] transition hover:bg-[#2B7FFF]/15 disabled:opacity-70"
              >
                <FiUserPlus className="h-4 w-4" />
                {`${t("ccAssignSelectedTickets")} (${selectedTicketIds.length})`}
              </button>
            ) : null}
            <button
              type="button"
              onClick={requestBulkDelete}
              disabled={selectedTicketIds.length === 0 || deletingSelected}
              className={`${canManageAssignment ? "" : "ml-auto "}inline-flex h-10 items-center gap-1.5 rounded-xl border-0 bg-red-600 px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:opacity-70`}
            >
              <FiTrash2 className="h-4 w-4" />
              {deletingSelected ? t("ccDeleting") : `${t("ccDeleteSelected")} (${selectedTicketIds.length})`}
            </button>
          </div>
        </section>

        <section className={LISTING_CARD_CLASS}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm text-left">
              <thead className={LISTING_TABLE_HEAD_CLASS}>
                <tr>
                  <th className={`${LISTING_TABLE_HEADER_CELL_CLASS} w-12`}>
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={toggleSelectAllVisible}
                        aria-label={t("ccSelectAllTickets")}
                        className="h-4 w-4 rounded border-slate-300 text-[#1d4ed8] focus:ring-[#1d4ed8]"
                      />
                    </th>
                  {displayListColumns.map((column) => (
                    <th key={column.fieldKey} className={LISTING_TABLE_HEADER_CELL_CLASS}>
                      {column.displayLabel}
                    </th>
                  ))}
                  <th className={`${LISTING_TABLE_HEADER_CELL_CLASS} w-24 text-right`}>
                    {t("ccActions")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {listLoading && tickets.length === 0 ? (
                  <TableSkeleton rows={5} columns={skeletonColumnCount} />
                ) : tickets.length === 0 ? (
                  <tr>
                    <td colSpan={skeletonColumnCount}>
                      <ListingEmptyState
                        icon={FiInbox}
                        title={t("ccNoTicketsFound")}
                        hint={hasActiveFilters ? t("ccNoTicketsFiltered") : t("ccNoTicketsEmpty")}
                        action={
                          !hasActiveFilters ? (
                            <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                              <Link to="/call-center/agent-workspace" className={LISTING_PRIMARY_BUTTON_CLASS}>
                                <FiPlus className="h-4 w-4" />
                                {t("ccNewTicket")}
                              </Link>
                              <Link
                                to="/call-center/ticket-bulk-upload"
                                className="inline-flex h-10 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                              >
                                <FiUpload className="h-4 w-4" />
                                {t("ccMenuTicketBulkUpload")}
                              </Link>
                            </div>
                          ) : null
                        }
                      />
                    </td>
                  </tr>
                ) : (
                  tickets.map((ticket) => {
                    const draft = draftUpdates[ticket.id] || {};
                    const statusValue = draft.status || ticket.statusKey || ticket.status || "";
                    const statusMeta = resolveTicketStatusMeta(statusLookup, statusValue, ticket.status);
                    const ticketPhone = resolveTicketPhoneNumber(ticket, getTicketFieldValue);
                    return (
                      <tr
                        key={ticket.id}
                        ref={highlightTicketId && String(ticket.id) === highlightTicketId ? highlightRowRef : undefined}
                        onClick={() => openTicketDetails(ticket)}
                        className={`${LISTING_TABLE_ROW_CLASS} cursor-pointer ${highlightTicketId && String(ticket.id) === highlightTicketId
                          ? "bg-blue-50/80 ring-2 ring-inset ring-[#1d4ed8]"
                          : ""
                          }`}
                      >
                        <td className="px-4 py-4 align-middle">
                          <input
                            type="checkbox"
                            checked={selectedTicketIds.includes(ticket.id)}
                            onChange={() => toggleTicketSelection(ticket.id)}
                            onClick={(event) => event.stopPropagation()}
                            aria-label={`${t("ccSelectTicket")} ${ticket.id}`}
                            className="h-4 w-4 rounded border-slate-300 text-[#1d4ed8] focus:ring-[#1d4ed8]"
                          />
                        </td>
                        {displayListColumns.map((column) => {
                          const rawValue = getTicketFieldValue(ticket, column.fieldKey, "");
                          if (column.fieldKey === "status") {
                            return (
                              <td key={`${ticket.id}-${column.fieldKey}`} className="px-4 py-4 align-middle">
                                <TicketStatusBadge
                                  label={statusMeta.label}
                                  colorCode={statusMeta.colorCode}
                                />
                              </td>
                            );
                          }
                          if (column.fieldKey === "accountNo") {
                            return (
                              <td key={`${ticket.id}-${column.fieldKey}`} className="px-4 py-4 align-middle">
                                <div className="font-medium text-slate-800">{rawValue || t("ccEmDash")}</div>
                              </td>
                            );
                          }
                          if (column.fieldKey === "priority") {
                            const priorityMeta = priorityBadgeMeta(rawValue, t("ccEmDash"));
                            return (
                              <td key={`${ticket.id}-${column.fieldKey}`} className="px-4 py-4 align-middle">
                                <StatusBadge status={priorityMeta.status} label={priorityMeta.label} />
                              </td>
                            );
                          }
                          if (column.fieldKey === "targetOutstanding") {
                            const amount = formatOmrAmount(rawValue);
                            const remarks =
                              getTicketFieldValue(ticket, "remarks", "") ||
                              ticket.remarksComment ||
                              ticket.remarks ||
                              "";
                            return (
                              <td key={`${ticket.id}-${column.fieldKey}`} className="px-4 py-4 align-middle max-w-[320px]">
                                <div className="truncate" title={`${amount} ${remarks}`.trim()}>
                                  {amount ? (
                                    <span className="font-semibold text-slate-800">{amount}</span>
                                  ) : (
                                    <span className="text-slate-400">{t("ccEmDash")}</span>
                                  )}
                                  {remarks ? (
                                    <span className="text-slate-400"> {remarks}</span>
                                  ) : null}
                                </div>
                              </td>
                            );
                          }
                          if (
                            column.fieldKey === "mobileNumber" ||
                            column.fieldKey === "mobileNo" ||
                            column.fieldKey === "phoneNumber"
                          ) {
                            return (
                              <td key={`${ticket.id}-${column.fieldKey}`} className="px-4 py-4 align-middle text-slate-700">
                                {rawValue ? formatPhoneDisplay(rawValue) : t("ccEmDash")}
                              </td>
                            );
                          }
                          if (column.fieldKey === "assignedTo" || column.fieldKey === "agentName") {
                            const assigneeLabel = resolveAssigneeLabel(ticket);
                            return (
                              <td key={`${ticket.id}-${column.fieldKey}`} className="px-4 py-4 align-middle text-sm text-slate-700">
                                <span className={ticket.assignedTo ? "font-medium text-slate-800" : "text-slate-400"}>
                                  {assigneeLabel}
                                </span>
                              </td>
                            );
                          }
                          return (
                            <td key={`${ticket.id}-${column.fieldKey}`} className="px-4 py-4 align-middle text-sm text-slate-700 max-w-[260px]">
                              <div className="truncate" title={String(rawValue)}>
                                {rawValue || t("ccEmDash")}
                              </div>
                            </td>
                          );
                        })}
                        <td className="px-4 py-4 align-middle text-right">
                          <div className="inline-flex items-center justify-end gap-1.5">
                            {canManageAssignment ? (
                              <button
                                type="button"
                                title={ticket.assignedTo ? t("ccReassignTicket") : t("ccAssignTicket")}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openAssignModal([ticket.id], {
                                    initialAssigneeId: ticket.assignedTo ?? "",
                                  });
                                }}
                                className={`${LISTING_ICON_ACTION_CLASS} hover:border-[#2B7FFF]/30 hover:bg-[#2B7FFF]/10 hover:text-[#1d4ed8]`}
                                aria-label={ticket.assignedTo ? t("ccReassignTicket") : t("ccAssignTicket")}
                              >
                                <FiUserPlus className="h-4 w-4" />
                              </button>
                            ) : null}
                            <button
                              type="button"
                              title={ticketPhone ? t("ccWhatsAppOpenChat") : t("ccWhatsAppNoPhone")}
                              disabled={!ticketPhone}
                              onClick={(event) => {
                                event.stopPropagation();
                                if (ticketPhone) {
                                  openTicketDetails(ticket, { focusWhatsApp: true });
                                }
                              }}
                              className={`${LISTING_ICON_ACTION_CLASS} ${
                                ticketPhone
                                  ? "hover:border-[#25D366]/30 hover:bg-[#25D366]/10 hover:text-[#128C7E]"
                                  : "cursor-not-allowed opacity-40 hover:border-slate-200 hover:bg-transparent hover:text-slate-400"
                              }`}
                              aria-label={t("ccWhatsApp")}
                            >
                              <FaWhatsapp className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <ListingPaginationFooter
            totalElements={pagination.totalElements}
            resultsLabel={ticketResultsLabel}
            page={pagination.page}
            totalPages={pagination.totalPages}
            loading={listLoading}
            onPrevious={() => loadTickets(filters, Math.max(0, pagination.page - 1), pagination.size)}
            onNext={() => loadTickets(filters, pagination.page + 1, pagination.size)}
            previousLabel={t("ccPrevious")}
            nextLabel={t("ccNext")}
            extra={
              <select
                value={pagination.size}
                onChange={(event) => {
                  const nextSize = Number(event.target.value) || 20;
                  loadTickets(filters, 0, nextSize);
                }}
                className="h-9 rounded-xl border border-slate-200 bg-white px-2 text-xs text-slate-600"
                aria-label={t("ccRows")}
              >
                {[20, 50, 100, 200].map((sizeOption) => (
                  <option key={sizeOption} value={sizeOption}>
                    {sizeOption}
                  </option>
                ))}
              </select>
            }
          />
        </section>

        <CallCenterModal
          open={Boolean(activeTicket)}
          onClose={closeTicketDetails}
          maxWidth="4xl"
          maxHeight="calc(100vh - 3rem)"
          showCloseButton={false}
          contentVariant="none"
          bodyClassName="min-h-0 flex-1 overflow-y-auto overscroll-contain p-4 sm:p-6 space-y-6"
          header={
            activeTicket ? (
                  <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 sm:px-6">
                    <div>
                      <h3 className="text-lg font-bold text-[#111827]">
                        {t("ccTicket")} {activeTicket.accountNo ?? activeTicket.accountId ?? activeTicket.id}
                      </h3>
                      <p className="text-xs text-slate-500">
                        {t("ccTicketId")}: {activeTicket.id}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setPaymentProofOpen(true)}
                        className="h-9 rounded-xl border border-orange-200 bg-orange-50 px-4 text-sm font-semibold text-orange-800 hover:bg-orange-100"
                      >
                        {t("ccPaymentProofOpen")}
                      </button>
                      {canManageAssignment ? (
                        <button
                          type="button"
                          onClick={() =>
                            openAssignModal([activeTicket.id], {
                              initialAssigneeId: activeTicket.assignedTo ?? "",
                            })
                          }
                          className="h-9 rounded-xl border border-[#2B7FFF]/30 bg-[#2B7FFF]/10 px-4 text-sm font-semibold text-[#1d4ed8] hover:bg-[#2B7FFF]/15"
                        >
                          {activeTicket.assignedTo ? t("ccReassignTicket") : t("ccAssignTicket")}
                        </button>
                      ) : null}
                      {callingEnabled && (
                        <button
                          type="button"
                          onClick={() => handleMakeCall(activeTicket)}
                          disabled={callingTicketId === activeTicket.id || callLoading}
                          className="h-9 rounded-xl bg-[#2B7FFF] px-4 text-sm font-semibold text-white hover:bg-[#1d6ef2] disabled:opacity-50"
                        >
                          {callingTicketId === activeTicket.id ? t("ccProcessing") : t("ccMakeCall")}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={closeTicketDetails}
                        className="h-9 rounded-xl border border-slate-200 px-3 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                      >
                        {t("ccClose")}
                      </button>
                    </div>
                  </div>
            ) : null
          }
        >
          {activeTicket ? (
            <>
                    <div>
                      <h4 className="mb-3 text-sm font-semibold text-slate-800">{t("ccTicketOverview")}</h4>
                      <div className="grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                          <div className="text-xs text-slate-500">{t("ccPriority")}</div>
                          <div className="font-semibold text-slate-800">
                            {activeTicket.priority || t("ccEmDash")}
                          </div>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                          <div className="text-xs text-slate-500">{t("ccStatus")}</div>
                          <div className="mt-1">
                            {activeTicketStatusMeta ? (
                              <TicketStatusBadge
                                label={activeTicketStatusMeta.label}
                                colorCode={activeTicketStatusMeta.colorCode}
                              />
                            ) : (
                              t("ccEmDash")
                            )}
                          </div>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                          <div className="text-xs text-slate-500">{t("ccAssignee")}</div>
                          <div className="font-semibold text-slate-800">
                            {resolveAssigneeLabel(activeTicket)}
                          </div>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                          <div className="text-xs text-slate-500">{t("ccTargetOutstanding")}</div>
                          <div className="font-semibold text-slate-800">
                            {activeTicket.targetOutstanding ?? t("ccEmDash")}
                          </div>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                          <div className="text-xs text-slate-500">{t("ccPaymentDate")}</div>
                          <div className="font-semibold text-slate-800">
                            {formatDateTime(activeTicket.targetPaymentDate)}
                          </div>
                        </div>
                        {columnConfig
                          .filter((column) => !column.predefined && column.visibleInList)
                          .map((column) => (
                            <div
                              key={`detail-${column.fieldKey}`}
                              className="rounded-xl border border-slate-200 bg-slate-50 p-3"
                            >
                              <div className="text-xs text-slate-500">{column.displayLabel}</div>
                              <div className="font-semibold text-slate-800">
                                {getTicketFieldValue(activeTicket, column.fieldKey, t("ccEmDash"))}
                              </div>
                            </div>
                          ))}
                      </div>
                      {activeTicket.paymentConfirmationSource && (
                        <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50/80 p-4">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <FiCheckCircle className="h-5 w-5 text-emerald-600" />
                              <h5 className="text-sm font-semibold text-emerald-900">
                                {t("ccPaymentConfirmationInfoTitle")}
                              </h5>
                            </div>
                            <StatusBadge
                              status="ACTIVE"
                              label={t("ccPaymentConfirmationInfoBadge")}
                            />
                          </div>
                          <div className="mt-4 grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                            <div>
                              <div className={FILTER_LABEL_CLASS}>{t("ccPaymentConfirmationInfoSource")}</div>
                              <div className="font-semibold text-emerald-900">
                                {formatPaymentSource(activeTicket.paymentConfirmationSource)}
                              </div>
                            </div>
                            {activeTicket.paymentConfirmationReference ? (
                              <div>
                                <div className={FILTER_LABEL_CLASS}>
                                  {t("ccPaymentConfirmationInfoReference")}
                                </div>
                                <div className="break-all font-semibold text-emerald-900">
                                  {activeTicket.paymentConfirmationReference}
                                </div>
                              </div>
                            ) : null}
                            {activeTicket.paymentConfirmationAmount != null ? (
                              <div>
                                <div className={FILTER_LABEL_CLASS}>
                                  {t("ccPaymentConfirmationInfoAmount")}
                                </div>
                                <div className="font-semibold text-emerald-900">
                                  {formatPaymentAmount(activeTicket.paymentConfirmationAmount)}
                                </div>
                              </div>
                            ) : null}
                            {activeTicket.paymentConfirmedAt ? (
                              <div>
                                <div className={FILTER_LABEL_CLASS}>
                                  {t("ccPaymentConfirmationInfoConfirmedAt")}
                                </div>
                                <div className="font-semibold text-emerald-900">
                                  {formatDateTime(activeTicket.paymentConfirmedAt)}
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="border-t border-slate-100 pt-5">
                      <h4 className="mb-3 text-sm font-semibold text-slate-800">{t("ccTicketAttachments")}</h4>
                      <TicketAttachments ticketId={activeTicket.id} />
                    </div>

                    <div className="border-t border-slate-100 pt-5">
                      <TicketAssignmentHistory
                        ticketId={activeTicket.id}
                        refreshKey={assignmentHistoryRefreshKey}
                      />
                    </div>

                    <div className="border-t border-slate-100 pt-5">
                      <div className="mb-3 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setDetailTab("calls")}
                          className={`h-9 rounded-xl px-3 text-sm font-semibold ${
                            detailTab === "calls"
                              ? "bg-[#2B7FFF] text-white"
                              : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          {t("ccCallCommentThread")}
                        </button>
                        <button
                          type="button"
                          onClick={() => setDetailTab("whatsapp")}
                          className={`h-9 rounded-xl px-3 text-sm font-semibold ${
                            detailTab === "whatsapp"
                              ? "bg-[#25D366] text-white"
                              : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          {t("ccWhatsAppChat")}
                        </button>
                      </div>
                      {detailTab === "calls" ? (
                        <TicketCallThread
                          key={threadRefreshKey}
                          ticketId={activeTicket.id}
                          pendingRecordingCallId={pendingRecordingCallId}
                        />
                      ) : (
                        <div ref={whatsappSectionRef}>
                          <TicketWhatsAppThread
                            ticketId={activeTicket.id}
                            active={detailTab === "whatsapp"}
                            refreshKey={whatsappRefreshKey}
                          />
                        </div>
                      )}
                    </div>

                    <div className="border-t border-slate-100 pt-5">
                      <h4 className="mb-3 text-sm font-semibold text-slate-800">{t("ccUpdateTicket")}</h4>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        {isColumnVisibleInUpdate(columnConfig, "status") && (
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-slate-500">
                              {t("ccStatus")}
                            </label>
                            <select
                              value={
                                draftUpdates[activeTicket.id]?.status ||
                                activeTicket.statusKey ||
                                activeTicket.status ||
                                ""
                              }
                              onChange={(event) =>
                                updateDraft(activeTicket.id, { status: event.target.value })
                              }
                              className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15"
                              disabled={updatingTicketId === activeTicket.id}
                            >
                              {statusOptions.map((statusOption) => (
                                <option key={statusOption.id} value={statusOption.statusKey}>
                                  {statusOption.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}

                        <div>
                          <label className="mb-1 block text-xs font-semibold text-slate-500">
                            {t("ccPriority")}
                          </label>
                          <select
                            value={draftUpdates[activeTicket.id]?.priority || activeTicket.priority || ""}
                            onChange={(event) =>
                              updateDraft(activeTicket.id, { priority: event.target.value })
                            }
                            className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15"
                            disabled={updatingTicketId === activeTicket.id}
                          >
                            {Object.values(TicketPriority).map((priority) => (
                              <option key={`update-priority-${priority}`} value={priority}>
                                {priority}
                              </option>
                            ))}
                          </select>
                        </div>

                        {isColumnVisibleInUpdate(columnConfig, "remarks") && (
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-slate-500">
                              {t("ccRemarks")}
                            </label>
                            <input
                              type="text"
                              value={draftUpdates[activeTicket.id]?.remarksComment || ""}
                              onChange={(event) =>
                                updateDraft(activeTicket.id, { remarksComment: event.target.value })
                              }
                              placeholder={t("ccAddRemarkNote")}
                              className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15"
                              disabled={updatingTicketId === activeTicket.id}
                            />
                          </div>
                        )}

                        {isColumnVisibleInUpdate(columnConfig, "targetOutstanding") && (
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-slate-500">
                              {t("ccTargetOutstanding")}
                            </label>
                            <input
                              type="number"
                              step="any"
                              value={draftUpdates[activeTicket.id]?.targetOutstanding ?? ""}
                              onChange={(event) =>
                                updateDraft(activeTicket.id, {
                                  targetOutstanding: event.target.value,
                                })
                              }
                              placeholder="0.00"
                              className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15"
                              disabled={updatingTicketId === activeTicket.id}
                            />
                          </div>
                        )}

                        {isColumnVisibleInUpdate(columnConfig, "targetPaymentDate") && (
                          <div>
                            <label className="mb-1 block text-xs font-semibold text-slate-500">
                              {t("ccTargetPaymentDate")}
                            </label>
                            <input
                              type="date"
                              value={draftUpdates[activeTicket.id]?.targetPaymentDate || ""}
                              onChange={(event) =>
                                updateDraft(activeTicket.id, {
                                  targetPaymentDate: event.target.value,
                                })
                              }
                              className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15"
                              disabled={updatingTicketId === activeTicket.id}
                            />
                          </div>
                        )}

                        <div>
                          <label className="mb-1 block text-xs font-semibold text-slate-500">
                            {t("ccCalledAt")}
                          </label>
                          <input
                            type="datetime-local"
                            value={draftUpdates[activeTicket.id]?.calledAt || ""}
                            onChange={(event) =>
                              updateDraft(activeTicket.id, { calledAt: event.target.value })
                            }
                            className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm outline-none focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15"
                            disabled={updatingTicketId === activeTicket.id}
                          />
                        </div>

                        {updateColumns.map((column) =>
                          renderUpdateColumnInput(column, activeTicket.id)
                        )}

                        <div className="md:col-span-2">
                          <label className="mb-1 block text-xs font-semibold text-slate-500">
                            {t("ccAdditionalComments")}
                          </label>
                          <textarea
                            value={draftUpdates[activeTicket.id]?.additionalComments || ""}
                            onChange={(event) =>
                              updateDraft(activeTicket.id, {
                                additionalComments: event.target.value,
                              })
                            }
                            rows={4}
                            placeholder={t("ccAddAdditionalComments")}
                            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#2B7FFF] focus:ring-2 focus:ring-[#2B7FFF]/15"
                            disabled={updatingTicketId === activeTicket.id}
                          />
                        </div>
                      </div>

                      <div className="mt-4">
                        <button
                          type="button"
                          onClick={() => handleStatusUpdate(activeTicket)}
                          disabled={updatingTicketId === activeTicket.id}
                          className={`${LISTING_PRIMARY_BUTTON_CLASS} w-full justify-center disabled:cursor-not-allowed disabled:opacity-70`}
                        >
                          {updatingTicketId === activeTicket.id
                            ? t("ccSaving")
                            : t("ccSaveStatusUpdate")}
                        </button>
                      </div>
                    </div>
            </>
          ) : null}
        </CallCenterModal>

        {activeTicket && (
          <PaymentProofModal
            open={paymentProofOpen}
            ticketId={activeTicket.id}
            onClose={() => setPaymentProofOpen(false)}
          />
        )}

        <TicketAssignModal
          open={assignModalOpen}
          title={
            assignModalMode === "bulk"
              ? t("ccTicketBulkAssignModalTitle")
              : assignInitialAssigneeId
                ? t("ccTicketReassignModalTitle")
                : t("ccTicketAssignModalTitle")
          }
          description={
            assignModalMode === "bulk"
              ? t("ccTicketBulkAssignModalDescription")
              : t("ccTicketAssignModalDescription")
          }
          officeUsers={officeUsers}
          officeUsersLoading={officeUsersLoading}
          initialAssigneeId={assignInitialAssigneeId}
          loadingAgentsLabel={t("ccLoadingUsers")}
          noAgentsLabel={t("ccTicketAssignNoAgents")}
          allowUnassign={assignModalMode === "single" && Boolean(assignInitialAssigneeId)}
          loading={assignSubmitting}
          confirmLabel={t("ccTicketAssignConfirm")}
          unassignLabel={t("ccTicketUnassign")}
          cancelLabel={t("ccCancel")}
          selectAgentLabel={t("ccTicketAssignSelectAgent")}
          selectAgentPlaceholder={
            assignSubmitting ? t("ccSaving") : t("ccTicketAssignSelectAgentPlaceholder")
          }
          onConfirm={handleConfirmAssignment}
          onUnassign={handleUnassignTicket}
          onClose={closeAssignModal}
        />

        <ConfirmDialog
          open={bulkDeleteConfirmOpen}
          title={t("ccConfirmDialogTitle")}
          message={t("ccConfirmBulkDeleteTickets").replace("{count}", String(selectedTicketIds.length))}
          cancelLabel={t("ccCancel")}
          confirmLabel={t("ccConfirmDialogProceed")}
          processingLabel={t("ccProcessing")}
          variant="danger"
          loading={deletingSelected}
          onConfirm={confirmBulkDelete}
          onClose={closeBulkDeleteConfirm}
        />
      </div>
    </div>
  );
};

export default CallCenterTicketsListPage;